import React, { ChangeEvent, useCallback, useMemo } from "react";
import { TextArea } from "@alm/flow-ds/components";
import { OnApplyProps } from "elements/types";
import { useSelector } from "react-redux";
import { State } from "data";
import {
	createGetElementData,
	ElementComponentData,
} from "data/form/elementSelectors";
import { useStandardElement } from "components/Forms/Element";
import { useDebounceSetDataOnApply } from "hooks/useDebounceSetData";
import { TextAreaElement } from "records/Element";
import { DisplayOnlyValue } from "elements";

function TextareaOnApply({
	fid,
	vid,
	id,
	index,
	indexes,
	rowIndex,
	uuid,
	hint,
	label,
	required,
	disabled,
	maxLength,
	displayOnly,
	isApplyClicked,
	fieldsIsEditedStatus,
	setFieldsIsEditedStatus,
}: OnApplyProps<TextAreaElement>) {
	const getData = useMemo(createGetElementData, []);
	const selectorProps = useMemo(
		() => ({ fid, vid, id, index, indexes, rowIndex, uuid }),
		[fid, vid, id, index, indexes, rowIndex, uuid]
	);
	const data = useSelector((state: State) =>
		getData(state, selectorProps)
	) as ElementComponentData<string>;
	const { eid, setDataAndQueue } = useStandardElement(selectorProps);
	const { onChange, onBlur, value } = useDebounceSetDataOnApply(
		setDataAndQueue,
		data,
		isApplyClicked,
		fieldsIsEditedStatus[uuid]
	);

	const handleOnChange = useCallback(
		(event: ChangeEvent<HTMLTextAreaElement>) => {
			onChange(event.currentTarget.value);
			setFieldsIsEditedStatus(prev => ({ ...prev, [uuid]: true }));
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[onChange, uuid]
	);

	// Display only
	if (displayOnly) {
		return <DisplayOnlyValue label={label} value={value} />;
	}

	return (
		<TextArea
			id={eid}
			value={value.toString()}
			maxLength={maxLength}
			onChange={handleOnChange}
			onBlur={onBlur}
			disabled={disabled}
			status={data.status}
			label={label}
			feedback={data.feedback}
			hint={hint}
			required={required}
		/>
	);
}

export default React.memo(TextareaOnApply);
